import { cn } from '@rocco/utils/cn';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { useWebsiteVisited } from '~/modules/tracking/hook/useWebsiteVisited';
import { detailCta, type DetailCtaVariant } from './detail-cta';

export interface TrackedWebsiteCtaProps {
    website?: string | null;
    libraryItemID: number;
    professionalID: number;
    professionalTitle: string;
    /** Pill variant. Ignored when `asLink`; defaults to `outlined-light`. */
    variant?: DetailCtaVariant;
    /**
     * Render an underlined text link instead of a pill — for a sub-CTA under a
     * primary Enquire button (e.g. the hook / closing prompts). Keeps the same
     * referral tracking so the `Website` event still fires.
     */
    asLink?: boolean;
    /**
     * Source attribution on the `Website` event (e.g. "ProjectWebsiteCTA"),
     * so reporting can segment referrals by surface — matches the legacy
     * CTAs' attribution.
     */
    source?: string;
    className?: string;
    children?: React.ReactNode;
}

/**
 * "Visit Website" detail-page CTA with full referral tracking parity with the
 * legacy PDP CTA (`ProductDetailsCTA`): the href carries UTM + `__apid`
 * params and the rendered attributes let the tracker's global link-click
 * listener log the `Website` event. Renders nothing when there is no URL.
 * A pill by default, or an underlined text link with `asLink`.
 */
export const TrackedWebsiteCta = ({
    website,
    libraryItemID,
    professionalID,
    professionalTitle,
    variant = 'outlined-light',
    asLink = false,
    source,
    className,
    children = 'Visit Website',
}: TrackedWebsiteCtaProps) => {
    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(website, {
            libraryItemID,
            professionalID,
            professionalTitle,
            source,
        });
    const { markVisited } = useWebsiteVisited(libraryItemID);

    if (!referralTrackingURL) return null;

    return (
        <a
            href={referralTrackingURL}
            target="_blank"
            {...referralTrackingParams}
            rel="noopener"
            onClick={markVisited}
            className={
                asLink
                    ? cn(
                          'inline-flex items-center gap-1 font-sans underline underline-offset-2',
                          className,
                      )
                    : detailCta(variant, className)
            }
        >
            {children}
        </a>
    );
};
